import React from 'react';

import { IconProp } from '@fortawesome/fontawesome-svg-core';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { zodResolver } from '@hookform/resolvers/zod';
import { AxiosError } from 'axios';
import { useForm } from 'react-hook-form';
import { FormattedMessage } from 'react-intl';

import BulkSpaceForm from 'src/features/spaces/components/form/BulkSpaceForm';
import { bulkSpaceFormSchema, BulkSpaceFormSchemaValues } from 'src/features/spaces/utils/formSchema';
import { Space } from 'src/features/spaces/utils/types';
import SquareIcon from 'src/registry/components/blocks/SquareIcon';
import { DataTableNewEntryProps } from 'src/registry/components/table/types';
import { SmartParkError } from 'src/registry/types/axios';
import { Button } from 'src/registry/ui/Button';
import {
    Dialog,
    DialogBody,
    DialogClose,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogSeparator,
    DialogTitle,
    DialogTrigger,
} from 'src/registry/ui/Dialog';
import { generalMessages } from 'src/registry/utils/messages';
import { ICON_MAP } from 'src/utils/constants';
import { useOnError } from 'src/utils/errors';
import { cn } from 'src/utils/shadcn';

interface BulkSpaceFormModalProps<TData> extends DataTableNewEntryProps<TData> {
    selectedSpace?: Space;
    children?: React.ReactNode;
    isOpen?: boolean;
    isPending: boolean;
    icon: IconProp;
    title: string;
    subtitle: string;
    mutate: (
        data: BulkSpaceFormSchemaValues,
        onSuccess: () => void,
        onError: (error: AxiosError<SmartParkError, any>) => void,
    ) => void;
}

function BulkSpaceFormModal<TData>({
    selectedSpace,
    className,
    isPending,
    children,
    icon,
    title,
    subtitle,
    mutate,
}: BulkSpaceFormModalProps<TData>) {
    const [isModalOpen, setIsModalOpen] = React.useState(false);

    const onError = useOnError();

    const defaultValues = {
        disallow_usage_for: undefined,
        zone: '',
        start: undefined,
        end: undefined,
    };

    const form = useForm<BulkSpaceFormSchemaValues>({
        resolver: zodResolver(bulkSpaceFormSchema),
        defaultValues,
    });

    const onOpenChange = (isOpen: boolean) => {
        if (!isOpen) form.reset(defaultValues);

        setIsModalOpen(isOpen);
    };

    const onSubmit = React.useCallback(
        (values: BulkSpaceFormSchemaValues) => {
            const dirtyFields = form.formState.dirtyFields;

            const editedFields = Object.keys(dirtyFields).reduce(
                (acc, key) => {
                    const typedKey = key as keyof BulkSpaceFormSchemaValues;
                    acc[typedKey] = values[typedKey];
                    return acc;
                },
                {} as Record<keyof BulkSpaceFormSchemaValues, any>,
            );

            mutate(
                editedFields,
                () => {
                    setIsModalOpen(false);
                    form.reset(defaultValues);
                },
                (error) => onError(error, form.setError),
            );
        },
        [mutate, form, onError, setIsModalOpen, defaultValues],
    );

    return (
        <Dialog modal open={isModalOpen} onOpenChange={onOpenChange}>
            {children ? (
                <DialogTrigger asChild>{children}</DialogTrigger>
            ) : (
                <DialogTrigger asChild>
                    <Button className={cn('ml-auto', className)}>
                        <FontAwesomeIcon icon={ICON_MAP.ADD} />
                        {title}
                    </Button>
                </DialogTrigger>
            )}
            <DialogContent>
                <DialogHeader>
                    <div className="flex flex-row gap-2">
                        <SquareIcon icon={icon} />
                        <div>
                            <DialogTitle>{title}</DialogTitle>
                            <DialogDescription>{subtitle}</DialogDescription>
                        </div>
                    </div>
                </DialogHeader>
                <DialogSeparator />
                <DialogBody>
                    <BulkSpaceForm form={form} onSubmit={onSubmit} />
                </DialogBody>
                <DialogSeparator />
                <DialogFooter>
                    <DialogClose asChild>
                        <Button variant="outline" type="button" className="flex-1">
                            <FormattedMessage {...generalMessages.cancel} />
                        </Button>
                    </DialogClose>
                    <Button
                        variant="default"
                        disabled={isPending || (selectedSpace && !form.formState.isDirty)}
                        type="submit"
                        onClick={form.handleSubmit(onSubmit)}
                        className="flex-1"
                    >
                        {isPending && <FontAwesomeIcon icon={ICON_MAP.SPINNER} className="mr-2 size-4 animate-spin" />}
                        {title}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}

export default BulkSpaceFormModal;
